'use client';

import {
  IconCamera,
  IconCopyCheck,
  IconCopy,
  IconFolderExclamation,
  IconHistory,
  IconFileTypePdf,
  IconLoader,
  IconRefresh,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FetchOrderWorkProofReportResponseBody,
  FetchOrderWorkProofResponseBody,
  isCapturableWorkProof,
  OrderWorkProofDisplayStatus,
  OrderWorkProofInspectionSummary,
  PresignOrderWorkProofResponseBody,
} from 'bff';
import { format } from 'date-fns';
import { useTranslations } from 'i11n';
import React, { useCallback, useState } from 'react';
import { WretchError } from 'wretch/resolver';
import {
  Badge,
  BadgeProps,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useToast,
} from 'ui';
import { useApiError } from '../hooks/useApiError';
import { useClipboard } from '../hooks/useClipboardCopy';
import { buildOrderProgressQueryKey } from './OrderProgressHeader';
import { IFrameLoader } from './IFrameLoader';
import { InspectionReportDialog } from './InspectionReportDialog';
import { AutoinspectorLogoSVG } from './PlatformLogoSVG';

export const buildOrderWorkProofQueryKey = (orderId: string) => [
  `order-work-proof-${orderId}`,
];

// Record-based dispatch: every display status maps to a Badge variant. Adding a
// status to the contract breaks the build here until it is mapped.
const WORK_PROOF_STATUS_VARIANT: Record<
  OrderWorkProofDisplayStatus,
  BadgeProps['variant']
> = {
  pending_capture: 'warning',
  capturing: 'info',
  processing: 'info',
  approved: 'success',
  disapproved: 'danger',
  error: 'danger',
  blocked: 'neutral',
};

const formatDate = (date: string) => format(new Date(date), 'dd/MM/yyyy HH:mm');

export const OrderWorkProofStatusBadge = ({
  status,
}: {
  status: OrderWorkProofDisplayStatus;
}) => {
  const t = useTranslations('orders.workProof');

  return (
    <Badge variant={WORK_PROOF_STATUS_VARIANT[status]} className='w-fit'>
      {t(`status.${status}`)}
    </Badge>
  );
};

/**
 * Copies the magic link so it can be forwarded (WhatsApp) to whoever runs the
 * capture. Both audiences read it, and it only shows while the link still leads
 * somewhere: same rule as the QR.
 */
const CopyCaptureLinkButton = ({ magicLink }: { magicLink: string }) => {
  const t = useTranslations('orders.workProof');
  const clipboard = useClipboard({ copiedTimeout: 3000 });

  const copyLink = useCallback(
    () => clipboard.copy(magicLink),
    [clipboard.copy, magicLink],
  );

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size='xs'
            variant='ghost'
            className='px-1.5 rounded-md flex items-center gap-2'
            onClick={copyLink}
          >
            {clipboard.copied ? (
              <IconCopyCheck className='w-4 h-4' />
            ) : (
              <IconCopy className='w-4 h-4' />
            )}
            {/* <span className='text-xs'>
              {clipboard.copied ? t('linkCopied') : t('copyLink')}
            </span> */}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {clipboard.copied ? t('linkCopied') : t('copyLink')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * The title IS the way into the detail, for whoever can presign it (insurer).
 * The provider has no route for it, so it stays plain text — same criterion
 * that decides whether the fetcher is injected at all.
 */
const InspectionTitle = ({
  label,
  onOpenDetail,
}: {
  label: string;
  onOpenDetail?: () => void;
}) => {
  if (!onOpenDetail) return <>{label}</>;

  return (
    <button
      type='button'
      onClick={onOpenDetail}
      className='text-left hover:underline'
    >
      {label}
    </button>
  );
};

/**
 * The stored PDF, offered per inspection. Only rendered when the app injects the
 * fetcher, and disabled while there is nothing to open yet.
 */
const InspectionReportButton = ({
  inspection,
  onOpenReport,
}: {
  inspection: OrderWorkProofInspectionSummary;
  onOpenReport: (inspection: OrderWorkProofInspectionSummary) => void;
}) => {
  const t = useTranslations('orders.workProof.report');

  const openReport = useCallback(
    () => onOpenReport(inspection),
    [onOpenReport, inspection],
  );

  return (
    <Button
      size='xs'
      variant='ghost'
      className='px-2 rounded-xl'
      hidden={inspection.report_status === 'unavailable'}
      onClick={openReport}
    >
      <IconFileTypePdf className='w-4 h-4 mr-1.5' />
      {t('trigger')}
    </Button>
  );
};

const InspectionDates = ({
  inspection,
}: {
  inspection: OrderWorkProofInspectionSummary;
}) => {
  const t = useTranslations('orders.workProof');

  return (
    <span className='text-xs text-muted-foreground'>
      Fecha creación: {formatDate(inspection.created_at)}
      {inspection.completed_at
        ? ` — ${t('completedAt')}: ${formatDate(inspection.completed_at)}`
        : ''}
    </span>
  );
};

/**
 * Work proof panel (Inspections tab), shared between insurer-app and
 * provider-app. Fetchers arrive as props (DeliverableReviewList pattern): each
 * app injects its own lib functions and slots — the capture QR is only mounted
 * by provider-app (`renderCapture`) and the integration state only by
 * insurer-app (`renderConfig`). The "Crear inspección" button lives outside, in
 * the header of the insurer section.
 *
 * "Actualizar" and "Ver inspección" are INSURER actions: both go out with their
 * API Key and consume their quota. The permission is expressed by injecting (or
 * not) the fetchers — provider-app passes neither, so the buttons never render,
 * and server side there are no provider routes for those operations.
 *
 * Generic in the response because the insurer route returns a superset
 * (availability + button enablement) that its slots consume without casts.
 */
export const OrderWorkProofPanel = <T extends FetchOrderWorkProofResponseBody>({
  orderId,
  initialData,
  emptyMessage,
  fetchWorkProof,
  refreshWorkProof,
  presignWorkProof,
  fetchInspectionReport,
  renderCapture,
  renderConfig,
}: {
  orderId: string;
  initialData?: T;
  // The empty state differs by audience and by whether the order requires the
  // proof at all, so each app resolves the copy.
  emptyMessage: string;
  fetchWorkProof: () => Promise<T>;
  refreshWorkProof?: () => Promise<T>;
  // Per inspection: the detail opens for the active one and for the history.
  presignWorkProof?: (
    inspectionId: string,
  ) => Promise<PresignOrderWorkProofResponseBody>;
  // Injected only by the apps allowed to read the stored report: provider-app
  // passes neither this nor the presign, so it gains no access by accident.
  fetchInspectionReport?: (
    inspectionId: string,
  ) => Promise<FetchOrderWorkProofReportResponseBody>;
  renderCapture?: (args: {
    active: NonNullable<FetchOrderWorkProofResponseBody['active']>;
  }) => React.ReactNode;
  renderConfig?: (args: { workProof: T }) => React.ReactNode;
}) => {
  const t = useTranslations('orders.workProof');
  const { toast } = useToast();
  const { handleError } = useApiError();
  const queryClient = useQueryClient();

  // The inspection open in the dialog: with a history a boolean is not enough,
  // the title has to say which one is being looked at.
  const [detailInspection, setDetailInspection] =
    useState<OrderWorkProofInspectionSummary>();

  const [reportInspection, setReportInspection] =
    useState<OrderWorkProofInspectionSummary>();

  const query = useQuery({
    queryKey: buildOrderWorkProofQueryKey(orderId),
    queryFn: fetchWorkProof,
    initialData,
  });

  const refresh = useMutation({
    mutationFn: () => refreshWorkProof!(),
    onSuccess: (data) => {
      queryClient.setQueryData(buildOrderWorkProofQueryKey(orderId), data);
      // A refresh can resolve the inspection, and the header derives the
      // completion gate from it.
      queryClient.invalidateQueries(buildOrderProgressQueryKey(orderId));
      toast({ title: t('refreshSuccess') });
    },
    onError: (error) => handleError(error as WretchError),
  });

  // The presigned URL expires: requested on open and never cached.
  const presign = useMutation({
    mutationFn: (inspectionId: string) => presignWorkProof!(inspectionId),
    onError: (error) => handleError(error as WretchError),
  });

  const openDetail = useCallback(
    (inspection: OrderWorkProofInspectionSummary) => {
      // Without the reset, opening another inspection shows the previous iframe
      // while the new URL is being signed.
      presign.reset();
      setDetailInspection(inspection);
      presign.mutate(inspection.id);
    },
    [presign],
  );

  const closeDetail = useCallback(() => setDetailInspection(undefined), []);

  const openReport = useCallback(
    (inspection: OrderWorkProofInspectionSummary) =>
      setReportInspection(inspection),
    [],
  );

  const closeReport = useCallback(
    (open: boolean) => !open && setReportInspection(undefined),
    [],
  );

  const data = query.data ?? initialData;

  if (!data) return null;

  const { active, history } = data;

  if (!active) {
    return (
      <div className='flex flex-col gap-3 bg-white mx-2'>
        {renderConfig?.({ workProof: data })}
        <div className='text-center text-sm text-muted-foreground flex items-center justify-center gap-2 py-4'>
          <IconFolderExclamation className='w-4 h-4' />
          <span>{emptyMessage}</span>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-3 bg-white mx-2'>
      {renderConfig?.({ workProof: data })}
      <div className='flex flex-col gap-3 bg-white rounded-xl border p-4'>
        {active && (
          <div className='flex justify-between items-center gap-2 flex-wrap'>
            <div className='flex flex-col gap-1.5'>
              <div className='flex items-start gap-2'>
                {/* <IconCamera className='w-5 h-5 shrink-0' /> */}
                <span className='mt-0.5'>
                  <AutoinspectorLogoSVG size={18} />
                </span>
                <h2 className='font-semibold text-base leading-tight break-all mt-0.5'>
                  <InspectionTitle
                    label={t('title', { id: active.external_id })}
                    onOpenDetail={
                      presignWorkProof ? () => openDetail(active) : undefined
                    }
                  />
                </h2>{' '}
                {fetchInspectionReport && (
                  <InspectionReportButton
                    inspection={active}
                    onOpenReport={openReport}
                  />
                )}
                {active.magic_link &&
                  isCapturableWorkProof(active.display_status) && (
                    <CopyCaptureLinkButton magicLink={active.magic_link} />
                  )}
              </div>

              <InspectionDates inspection={active} />
              {renderCapture?.({ active })}
            </div>

            <div className='flex items-center gap-1.5'>
              {refreshWorkProof && (
                <Button
                  size='xs'
                  variant='ghost'
                  onClick={() => refresh.mutate()}
                >
                  {refresh.isLoading ? (
                    <IconLoader className='w-4 h-4 animate-spin' />
                  ) : (
                    <IconRefresh className='w-4 h-4' />
                  )}
                </Button>
              )}
              <OrderWorkProofStatusBadge status={active.display_status} />
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div className='flex flex-col gap-1.5 border-t pt-3'>
            <h3 className='font-medium text-sm'>
              <IconHistory className='w-4 h-4 inline-block mr-1' />
              {t('history')}
            </h3>
            {history.map((inspection) => (
              <div
                key={inspection.id}
                className='flex flex-col lg:flex-row lg:items-center justify-between gap-2 mt-1'
              >
                <div className='flex flex-col gap-1 w-full'>
                  <div className='flex items-center justify-between gap-2'>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <span className='text-sm font-medium break-all'>
                        <InspectionTitle
                          label={t('title', { id: inspection.external_id })}
                          onOpenDetail={
                            presignWorkProof
                              ? () => openDetail(inspection)
                              : undefined
                          }
                        />
                      </span>
                    </div>
                    <div className='flex items-center gap-1.5'>
                      {fetchInspectionReport && (
                        <InspectionReportButton
                          inspection={inspection}
                          onOpenReport={openReport}
                        />
                      )}
                      <OrderWorkProofStatusBadge
                        status={inspection.display_status}
                      />
                    </div>
                  </div>
                  <InspectionDates inspection={inspection} />
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog
          open={!!detailInspection}
          onOpenChange={(open) => !open && closeDetail()}
        >
          <DialogContent className='max-w-5xl'>
            <DialogHeader>
              <DialogTitle>
                {detailInspection
                  ? t('title', { id: detailInspection.external_id })
                  : t('detailTitle')}
              </DialogTitle>
              <DialogDescription>{t('detailDescription')}</DialogDescription>
            </DialogHeader>
            {presign.data && <IFrameLoader url={presign.data.url} />}
          </DialogContent>
        </Dialog>

        {fetchInspectionReport && (
          <InspectionReportDialog
            inspection={reportInspection ?? null}
            onOpenChange={closeReport}
            fetchReport={fetchInspectionReport}
          />
        )}
      </div>
    </div>
  );
};
